
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { configureSessionExpiry, getAuthErrorMessage } from "./AuthUtils";
import { Eye, EyeOff } from "lucide-react";

interface SignInFormProps {
  switchToMode: (mode: string) => void;
  setEmail: (email: string) => void;
  email: string;
}

const SignInForm = ({ switchToMode, setEmail, email }: SignInFormProps) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    if (!email || !password) {
      toast.error("Please enter both email and password");
      setLoading(false);
      return;
    }
    
    try {
      console.log("Attempting sign in with email:", email);
      
      // Sign in with email and password
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("Sign in error:", error.message);
        
        if (error.message.includes("Email not confirmed")) {
          toast.error("Your email has not been confirmed yet. Please check your inbox including spam folder.");
          switchToMode("confirmation");
        } else {
          throw error;
        }
      } else {
        // If "Stay Logged In" is not checked, set session expiry to 1 hour
        await configureSessionExpiry(stayLoggedIn);
        
        toast.success("Signed in successfully!");
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Authentication error:", error.message);
      toast.error(getAuthErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-4" autoComplete="on">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          name="email" 
          autoComplete="username email"
          placeholder="hello@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="h-12"
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Button
            variant="link"
            type="button"
            className="text-sm text-clustr-blue hover:underline p-0 h-auto"
            onClick={(e) => {
              e.preventDefault();
              switchToMode("reset-password");
            }}
          >
            Forgot password?
          </Button>
        </div>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            name="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-12 pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-12 w-12 text-clustr-mediumGray hover:text-clustr-darkGray"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </Button>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="stayLoggedIn" 
          checked={stayLoggedIn} 
          onCheckedChange={(checked) => setStayLoggedIn(checked as boolean)} 
        />
        <Label htmlFor="stayLoggedIn" className="cursor-pointer text-sm font-normal">
          Stay logged in for 30 days
        </Label>
      </div>
      
      <Button
        type="submit"
        className="w-full h-12 bg-clustr-blue hover:bg-blue-600 transition-colors"
        disabled={loading}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Signing in...
          </span>
        ) : (
          <>Sign in</>
        )}
      </Button>
    </form>
  );
};

export default SignInForm;
